<template>
    <!-- Additional Info Panel: additional fields used within the Event form -->
    <div>
        <v-row dense>
            <v-col cols="12" lg="4">
                <LabelField :label="$t('vorverkauf_reservation')">
                    <v-textarea
                        :value="record.standard_text_tickets"
                        :placeholder="$t('placeholder_event_additional_info_standard_text_tickets')"
                        rows="7"
                        dense
                        outlined
                        hide-details="auto"
                        counter
                        @input="onInput('standard_text_tickets', $event)"
                    />
                </LabelField>
                <LabelField :label="$t('link_vorverkauf')" class="mt-6">
                    <UrlField
                        :value="record.presales_link"
                        :placeholder="$t('placeholder_event_additional_info_presales_link')"
                        @input="onInput('presales_link', $event)"
                    />
                </LabelField>
                <LabelField :label="$t('preise_kosten')" class="mt-6">
                    <v-textarea
                        :value="record.ticketprices"
                        :placeholder="$t('placeholder_event_additional_info_ticketprices')"
                        rows="4"
                        dense
                        outlined
                        hide-details="auto"
                        @input="onInput('ticketprices', $event)"
                    />
                </LabelField>
            </v-col>
            <v-col cols="12" lg="4">
                <LabelField :label="$t('hauptzielgruppe')">
                    <TargetAudienceSelect
                        :value="record.target_audience_id"
                        :placeholder="$t('placeholder_event_additional_info_target_audience_id')"
                        :disabled="!record.canEditTargetAudience"
                        clearable
                        hide-details="auto"
                        @input="onInput('target_audience_id', $event)"
                    />
                </LabelField>
                <LabelField :label="$t('altersbereich')" class="mt-6">
                    <v-textarea
                        :value="record.typical_age_range"
                        rows="4"
                        dense
                        outlined
                        hide-details="auto"
                        :placeholder="$t('placeholder_typical_age_range')"
                        @input="onInput('typical_age_range', $event)"
                    />
                </LabelField>
                <LabelField :label="$t('accessibility_label')" :label-tip="$t('accessibility_label_tip')" class="mt-6">
                    <v-textarea
                        :value="record.accessibility"
                        rows="4"
                        dense
                        outlined
                        hide-details="auto"
                        :counter="1000"
                        :placeholder="$t('placeholder_accessibility')"
                        :rules="[getRule('maxLength')(1000)]"
                        @input="onInput('accessibility', $event)"
                    />
                </LabelField>
                <LabelField :label="$t('accessibility_link')">
                    <AccessibilityUrlField
                        :location-id="record.location_id"
                        :accessibility-url="record.accessibility_link"
                        hide-reload-btn
                        @input="onInput('accessibility_link', $event)"
                    />
                </LabelField>
            </v-col>
            <v-col cols="12" lg="4">
                <LabelField :label="$t('original_event_url')" :label-tip="$t('original_event_url_tip')">
                    <UrlField
                        :value="record.original_event_url"
                        :placeholder="$t('placeholder_url')"
                        :rules="[getRule('maxLength')(1000)]"
                        @input="onInput('original_event_url', $event)"
                    />
                </LabelField>
                <LabelField
                    :label="$t('weiterfuehrende_links')"
                    :label-tip="$t('weiterfuehrende_links_label_tip')"
                    class="mt-6"
                >
                    <MultiItemContainer
                        :value="record.external_links"
                        text-name="description"
                        item-name="url"
                        :show-type-field="false"
                        item-class="mb-2"
                        @input="onInput('external_links', $event)"
                    >
                        <template #text="slotProps">
                            <v-text-field
                                :value="slotProps.value"
                                :placeholder="$t('placeholder_event_additional_info_external_links_text')"
                                dense
                                outlined
                                hide-details="auto"
                                clearable
                                @input="slotProps.input"
                            />
                        </template>
                        <template #default="slotProps">
                            <UrlField
                                :value="slotProps.value"
                                :placeholder="$t('placeholder_event_additional_info_external_links_item')"
                                @input="slotProps.input"
                            />
                        </template>
                    </MultiItemContainer>
                </LabelField>
                <LabelField :label="$t('dokumente_flyer_pogrammheft')" class="mt-6">
                    <MultiItemContainer
                        :value="record.documents"
                        text-name="description"
                        item-name="id"
                        :show-type-field="false"
                        item-class="mb-2"
                        @input="onInput('documents', $event)"
                    >
                        <template #text="slotProps">
                            <v-text-field
                                :value="slotProps.value"
                                :placeholder="$t('placeholder_event_additional_info_documents_text')"
                                dense
                                outlined
                                hide-details="auto"
                                clearable
                                @input="slotProps.input"
                            />
                        </template>
                        <template #default="slotProps">
                            <FileUploadField
                                :value="slotProps.value"
                                :placeholder="$t('placeholder_event_additional_info_documents_item')"
                                @input="
                                    (value) => {
                                        slotProps.input(value);
                                        $emit('save');
                                    }
                                "
                            />
                        </template>
                    </MultiItemContainer>
                </LabelField>
            </v-col>
        </v-row>
        <v-row v-if="openingHoursVisible">
            <v-col>
                <h4 class="mt-5 mb-2">{{ $t('opening_hours') }}</h4>
                <p class="tgk-paragraph-text">{{ $t('opening_hours_event_tip') }}</p>
                <v-textarea
                    :value="record.opening_hours"
                    dense
                    :placeholder="$t('placeholder_schedule_opening_hours')"
                    rows="8"
                    outlined
                    hide-details="auto"
                    :counter="250"
                    :rules="[getRule('maxLength')(250)]"
                    @input="onInput('opening_hours', $event)"
                />
                <div class="d-flex">
                    <KpIcon icon-cls="info" color="grey" left size="24" style="width: 24px" />
                    <p class="tgk-paragraph-text" style="flex: 1">{{ $t('opening_hours_event_info') }}</p>
                </div>
            </v-col>
        </v-row>
    </div>
</template>

<script>
import { Event } from '@/models/Event';
import getRulesMixin from '@/mixins/get-form-rules';
import MultiItemContainer from '@/components/multi_item/Container';
import FileUploadField from '@/components/FileUploadField';
import TargetAudienceSelect from '@/components/target_audience/select';
import AccessibilityUrlField from '../form/AccessibilityUrlField.vue';

/**
 * Additional Info Panel: additional fields used within the Event form.
 * @see Event form view(s) where it is used as re-usable component.
 *
 * emits:
 * - save: When the entry needs to be saved (on file upload, e.g.)
 *
 */
export default {
    name: 'EventEditAdditionalInfo',
    components: {
        MultiItemContainer,
        FileUploadField,
        TargetAudienceSelect,
        AccessibilityUrlField,
    },
    mixins: [getRulesMixin],

    props: {
        record: {
            type: Event,
            required: true,
        },
    },

    computed: {
        openingHoursVisible() {
            return [Event.CLASS_PERMANENT_EXHIBITION].includes(this.record.event_class);
        },
    },

    methods: {
        onInput(field, value, autoSave = false) {
            this.record.set(field, value);
            if (autoSave) {
                this.$emit('save');
            }
        },
    },
};
</script>
<style scoped lang="scss"></style>
